import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useSelectedCompanyStore } from '../../stores/selectedCompany.store';
import { useUserCompaniesStore } from '../../stores/userCompanies.store';
import { useGetUserDocuments } from '../../services/dashboard.service';
import { useGetDocumentPresignedUrl } from '../../services/documents.service';
import { UserDocumentsTable } from './UserDocumentsTable';
import { UserCompanyRole } from '@monorepo/shared';

const getRoleBadgeConfig = (role: UserCompanyRole | undefined) => {
  switch (role) {
    case UserCompanyRole.VALIDATOR:
      return { className: 'bg-green-500 text-white', labelKey: 'dashboard.role.validator' };
    case UserCompanyRole.USER:
      return { className: 'bg-gray-400 text-white', labelKey: 'dashboard.role.user' };
    default:
      return { className: 'bg-gray-400 text-white', labelKey: 'dashboard.role.user' };
  }
};

export const UserDashboard = () => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const companies = useUserCompaniesStore((state) => state.companies);
  const currentUserRole = companies.find((c) => c.id === selectedCompanyId)?.role;
  const badgeConfig = getRoleBadgeConfig(currentUserRole);

  const { data: userDocs, isLoading } = useGetUserDocuments(selectedCompanyId ?? '');
  const getPresignedUrlMutation = useGetDocumentPresignedUrl(selectedCompanyId ?? '');

  const handleOpenDocument = async (nodeId: string) => {
    try {
      const result = await getPresignedUrlMutation.mutateAsync(nodeId);
      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-display text-primary">
            {t('dashboard.greeting', { name: user?.firstName })}
          </h1>
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${badgeConfig.className}`}>
            {t(badgeConfig.labelKey)}
          </span>
        </div>
      </div>

      <UserDocumentsTable
        documents={userDocs?.documents ?? []}
        isLoading={isLoading}
        onOpenDocument={handleOpenDocument}
      />
    </div>
  );
};
